import { __rest } from '../../node_modules/tslib/tslib.es6.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { createRef, useState, useMemo, useCallback } from 'react';
import { Input } from '../input/input.component.js';
import { Text } from '../text/text.component.js';
import { FilteredSearchInputGroup, Dropdown, FilterRow, FilterTag } from './filtered-search-input.style.js';
import { useKeyboardListNavigation, parseFilter } from './filtered-search-input.utils.js';

const FilteredSearchInput = props => {
    const { clearButtonLabel = 'clear', className, filtering, filterOptions, filterTitle = 'select filter', icon = 'UISearch', margin, noResultsMessage, onChange, onEnter, onSelectResult, placeholder = 'Search...', results = [], type = 'text', value } = props, rest = __rest(props, ["clearButtonLabel", "className", "filtering", "filterOptions", "filterTitle", "icon", "margin", "noResultsMessage", "onChange", "onEnter", "onSelectResult", "placeholder", "results", "type", "value"]);
    const ref = createRef();
    const dropdownRef = createRef();
    const [focused, setFocused] = useState(false);
    const { handleArrowDown: filtersHandleArrowDown, handleArrowUp: filtersHandleArrowUp, resetIndex: resetFiltersIndex, selectedIndex: selectedFilterIndex, } = useKeyboardListNavigation();
    const { handleArrowDown: resultsHandleArrowDown, handleArrowUp: resultsHandleArrowUp, resetIndex: resetResultsIndex, selectedIndex: selectedResultIndex, } = useKeyboardListNavigation();
    const matchingFilters = useMemo(() => filterOptions.filter(option => option.tag.startsWith((value !== null && value !== void 0 ? value : '').toLowerCase())), [value, filterOptions]);
    const filterValue = parseFilter(value)[1];
    const showFilterDropdown = focused && !!matchingFilters.length && !results.length;
    const showResults = focused && !!results.length;
    const showNoResultsMessage = !!(noResultsMessage === null || noResultsMessage === void 0 ? void 0 : noResultsMessage.length) &&
        focused &&
        !results.length &&
        !!(filterValue === null || filterValue === void 0 ? void 0 : filterValue.length) &&
        !filtering;
    const showResultsDropdown = showResults || showNoResultsMessage;
    const reset = useCallback((shouldBlurHtmlInput = true) => {
        var _a;
        resetFiltersIndex();
        resetResultsIndex();
        setFocused(false);
        if (shouldBlurHtmlInput) {
            (_a = ref.current) === null || _a === void 0 ? void 0 : _a.blur();
        }
    }, [resetFiltersIndex, resetResultsIndex, ref]);
    const handleSelectFilter = useCallback((filter) => {
        var _a;
        onChange(`${filter.tag}: `, filter.tag, '');
        (_a = ref.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, [onChange, ref]);
    const handleSelectResult = useCallback((result) => {
        onSelectResult === null || onSelectResult === void 0 ? void 0 : onSelectResult(result);
        reset();
    }, [onSelectResult, reset]);
    const handleEnter = useCallback(() => {
        if (showFilterDropdown && selectedFilterIndex >= 0) {
            handleSelectFilter(matchingFilters[selectedFilterIndex]);
        }
        else if (showResults && selectedResultIndex >= 0) {
            handleSelectResult(results[selectedResultIndex]);
        }
        else {
            const [filterTag, filterValue] = parseFilter(value);
            onEnter(value !== null && value !== void 0 ? value : '', filterTag, filterValue);
            reset();
        }
    }, [
        handleSelectFilter,
        handleSelectResult,
        matchingFilters,
        onEnter,
        reset,
        results,
        selectedFilterIndex,
        selectedResultIndex,
        showFilterDropdown,
        showResults,
        value,
    ]);
    const handleOnKeyDown = useCallback((event) => {
        switch (event.key) {
            case 'Enter':
                handleEnter();
                break;
            case 'ArrowDown':
                if (showFilterDropdown)
                    filtersHandleArrowDown(matchingFilters.length);
                if (showResults)
                    resultsHandleArrowDown(results.length);
                break;
            case 'ArrowUp':
                if (showFilterDropdown)
                    filtersHandleArrowUp(matchingFilters.length);
                if (showResults)
                    resultsHandleArrowUp(results.length);
                break;
            case 'Escape':
            case 'Tab':
                setFocused(false);
                reset(false);
                break;
        }
    }, [
        filtersHandleArrowDown,
        filtersHandleArrowUp,
        handleEnter,
        matchingFilters.length,
        reset,
        results.length,
        resultsHandleArrowDown,
        resultsHandleArrowUp,
        showFilterDropdown,
        showResults,
    ]);
    const handleChange = useCallback((value) => {
        const [filterTag, filterValue] = parseFilter(value);
        onChange(value, filterTag, filterValue);
    }, [onChange]);
    const handleFocus = useCallback(() => setFocused(true), []);
    const handleBlur = useCallback((_, event) => {
        var _a;
        // hide dropdown when input blurs, unless an element in the dropdown was clicked
        if ((_a = dropdownRef === null || dropdownRef === void 0 ? void 0 : dropdownRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.relatedTarget))
            return;
        setFocused(false);
    }, [dropdownRef]);
    const handleClear = useCallback(() => {
        handleChange('');
        reset();
    }, [handleChange, reset]);
    return (jsxs(FilteredSearchInputGroup, Object.assign({ className: className, margin: margin }, { children: [jsx(Input, Object.assign({ type: type, icon: icon, placeholder: placeholder, iconButtonProps: (value === null || value === void 0 ? void 0 : value.length)
                    ? {
                        'aria-label': clearButtonLabel,
                        icon: 'UIClose',
                        color: 'primary',
                        onClick: handleClear,
                    }
                    : undefined, onFocus: handleFocus, onKeyDown: handleOnKeyDown, onBlur: handleBlur, value: value, onChange: handleChange, ref: ref }, rest), void 0), showFilterDropdown && (jsxs(Dropdown, Object.assign({ ref: dropdownRef }, { children: [jsx(Text, Object.assign({ textStyle: "eyebrow400", color: "secondary", margin: "0px 8px 8px" }, { children: filterTitle }), void 0), matchingFilters.map((option, index) => (jsxs(FilterRow, Object.assign({ onClick: () => handleSelectFilter(option), onMouseDown: (e) => e.preventDefault(), selected: index === selectedFilterIndex }, { children: [jsxs(FilterTag, { children: [option.tag, ":"] }, void 0), jsx(Text, Object.assign({ color: "secondary", margin: "0px 8px" }, { children: option.description }), void 0)] }), option.tag)))] }), void 0)), showResultsDropdown && (jsxs(Dropdown, Object.assign({ ref: dropdownRef }, { children: [results.map((result, index) => (jsxs(FilterRow, Object.assign({ onClick: () => handleSelectResult(result), onMouseDown: (e) => e.preventDefault(), selected: index === selectedResultIndex }, { children: [jsx(Text, Object.assign({ color: "primary" }, { children: result.primaryText }), void 0), jsx(Text, Object.assign({ color: "secondary", margin: "0px 8px" }, { children: result.secondaryText }), void 0)] }), result.id))), showNoResultsMessage && (jsx(FilterRow, Object.assign({ disabled: true, onMouseDown: (e) => e.preventDefault(), selected: false }, { children: jsx(Text, Object.assign({ color: "secondary" }, { children: noResultsMessage }), void 0) }), void 0))] }), void 0))] }), void 0));
};

export { FilteredSearchInput };
